import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import LoaderOverlay from "components/ProjectNonSpecificComponents/Elements/Loader/Loader"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import {
	IRequestTableFormField,
	ISavedRequestFormField,
} from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import {
	IRequestTableData,
	TSubmitRequest,
} from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import React, { useContext, useState } from "react"
import { useEffect } from "react"
import styled from "styled-components"
import AppRequests from "../../../API/AppRequests/AppRequests"
import SavedRequestFormModal from "../../Global/molecules/Modals/SavedRequestFormModal"
import RequestTableFormFieldTable from "./RequestTableFormFieldTable"
import { stripHtml } from "string-strip-html"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import { TOnClickTableRow } from "components/ProjectSpecificComponents/Interfaces/TableInterface"
import { observer } from "mobx-react"
import FormFieldLabel from "../../AdminForm/atoms/FormFieldLabel"
import { useTranslation } from "react-i18next"
import { UserContext } from "components/ProjectSpecificComponents/Contexts/UserContext"
import { lostOrFinishedUserSession } from "components/ProjectSpecificComponents/API/Functions/lostOrFinishedUserSession"
import LoadingText from "components/ProjectNonSpecificComponents/Elements/Loader/LoadingText"

interface Props {}

type TParsedTableContent = {
	ticketItemId: string | undefined
	name: string | undefined
	projectId: string | undefined
	savedRequestFormFields: ISavedRequestFormField[]
}

const defaultParsedTableContent = {
	ticketItemId: undefined,
	name: undefined,
	projectId: undefined,
	savedRequestFormFields: [],
}

const UserRequestTable = observer(({}: Props) => {
	const store = useStore()
	const formStore = useFormStore()
	const { t } = useTranslation()
	const { setUser } = useContext(UserContext)

	const [selectedItemIdx, setSelectedItemIdx] = useState<number | undefined>()
	const [tableConfig, setTableConfig] = useState<IRequestTableFormField[]>()
	const [requestTableData, setRequestTableData] = useState<IRequestTableData[]>()
	const [isLoading, setIsLoading] = useState<boolean>(true)
	const [parsedTableContent, setParsedTableContent] =
		useState<TParsedTableContent>(defaultParsedTableContent)

	useEffect(() => {
		if (formStore.userSelectedForm != null && selectedItemIdx != null && requestTableData) {
			let { collections, savedRequestForm } = formStore.userSelectedForm

			const picklistFieldId = collections[0]?.picklistFieldId

			let { id, name, fields, projectId } = requestTableData[selectedItemIdx]

			let { value: selectedPickListValue } =
				fields.find(({ fieldId }) => fieldId == picklistFieldId) || {}

			let savedRequestFormFields: ISavedRequestFormField[] = savedRequestForm.map(
				({ mainFieldKey, mainFieldLabel, nameEN, nameLV, fieldType }) => {
					// Get field's value from tool request's BE data by appropriate key
					let { value = "", color = "" } =
						fields.find(({ fieldId }) => fieldId == mainFieldKey) || {}

					value = value.toString()

					// Strip HTML which is present in "RICHTEXT" type values
					value = stripHtml(value).result

					return {
						mainFieldKey,
						mainFieldLabel,
						value,
						fieldType,
						nameEN,
						nameLV,
						color,
					}
				}
			)
			setParsedTableContent({ ticketItemId: id, name, projectId, savedRequestFormFields })
		} else {
			setParsedTableContent(defaultParsedTableContent)
		}
	}, [formStore.userSelectedForm, selectedItemIdx, requestTableData])

	const onClickTableRow: TOnClickTableRow = (rowId, row) => {
		if (requestTableData == null) return

		// Find which index the item is inside the data array
		// by its unique and guaranteed `documentKey` property.
		let { documentKey: desiredDocumentKey } = row
		let selectedIdx = requestTableData.findIndex(({ fields }) => {
			let foundDocumentKey = fields.find(
				({ fieldId }: TSubmitRequest) => fieldId == "documentKey"
			)?.value

			if (foundDocumentKey == desiredDocumentKey) return true
			return false
		})
		if (selectedIdx != -1) setSelectedItemIdx(selectedIdx)
	}

	useEffect(() => {
		if (store.selectedUserUISection != "myRequests") return
		const fetchTableData = async () => {
			setIsLoading(true)
			await Promise.all([getAndSetTableConfig(), getAndSetTableRecords()])
			store.triggerSessionTimeout()
			setIsLoading(false)
		}
		fetchTableData()
	}, [store.selectedUserUISection, formStore.userSelectedForm?.id])

	const getAndSetTableConfig = async () => {
		if (!formStore.userSelectedForm?.id) return setTableConfig(undefined)

		const { ok, data, statusCode } = await AppRequests("RequestForms").get<IRequestTableFormField[]>(
			"requestTable",
			{ requestedItemId: formStore.userSelectedForm?.id }
		)

		if (ok && data) {
			setTableConfig(data)
		} else if (statusCode == 401) {
			setUser(undefined)
			lostOrFinishedUserSession(store)
		}
	}

	const getAndSetTableRecords = async () => {
		if (!formStore.userSelectedForm?.id) return setRequestTableData(undefined)

		const { ok, data } = await AppRequests("Requests").get<IRequestTableData[]>(undefined, {
			requestedItemId: formStore.userSelectedForm?.id,
		})

		if (ok && data) {
			setRequestTableData(data)
		}
	}

	if (store.isLoaderOverlayOpen){
		return <></>
	}

	return (
		<>
			<HyperDiv
				centered={isLoading}
				centeredX={!isLoading}
				padding="10px 0px"
				height="calc(100%)"
				width="100%"
				style={{ overflowY: "auto" }}
			>
				{isLoading && <LoadingText />}
				{!isLoading && tableConfig && requestTableData && (
					<HyperDiv width="96%">
						<RequestTableFormFieldTable
							config={tableConfig}
							records={requestTableData}
							onClickRow={onClickTableRow}
						/>
					</HyperDiv>
				)}
				{/* Request fails */}
				{!isLoading && (!tableConfig || !requestTableData) && (
					<HyperDiv style={{ marginTop: 50, textAlign: "center" }}>
						<FormFieldLabel>{t("system.failed-system")}</FormFieldLabel>
					</HyperDiv>
				)}
			</HyperDiv>
			{selectedItemIdx != null && (
				<SavedRequestFormModal
					mode="submit"
					projectId={parsedTableContent.projectId}
					itemId={parsedTableContent.ticketItemId}
					fields={parsedTableContent.savedRequestFormFields}
					onClose={() => setSelectedItemIdx(undefined)}
					fadeBackground
				/>
			)}
		</>
	)
})

export default UserRequestTable

export const UserReqContainer = styled.div`
	width: auto;
	margin-top: 20px;
	padding: 0px 100px;
	padding-bottom: 3.5rem;
`
